import useFetch from '@hooks/useFetch';
import { emptyObject } from '@constants/empty';
import { getSkuDetailsAPIKey, isCbtProgramme } from '@cont/SeeAll/utils';
import { HOST_TYPE } from '@utils/api-helper/host';
import useTestSeriesStore from '@stores/test-series';
import { useRouter } from 'next/router';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useGoalSubscription from './useGoalSubscription';
import useTestSeriesInfo from './useTestSeriesInfo';

const TEST_SERIES_CONTENT_TYPE = 501;

const useSkuDetails = (
  goalUid,
  testSeriesUID,
  options = {
    revalidateOnMount: false,
    fallbackData: undefined
  }
) => {
  const { programmeType } = useTestSeriesInfo().data;
  const isCbt = isCbtProgramme(programmeType);
  const { isSubscribed: isGoalSubscribed, isSpecial } = useGoalSubscription();

  const key = getSkuDetailsAPIKey(testSeriesUID, goalUid);
  const { updateIsSkuFetched, updateIsUamFetched } = useTestSeriesStore();

  const router = useRouter();
  const { query } = router;
  const isTestSeriesPage = router.pathname.includes('test-series');
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const getAction = () => {
    if (isTestSeriesPage) {
      return 'Programme:TestSeriesView';
    }
    return '';
  };
  const action = getAction();
  const shouldFetch = !!goalUid && !!testSeriesUID;
  const uamKey = `v1/user/check_access/?actions=${action}&resource=Programme:${
    testSeriesUID ?? query.courseUID
  }`;

  const {
    data = {},
    isValidating,
    mutate,
    ...rest
  } = useFetch(
    shouldFetch ? key : null,
    { hostType: HOST_TYPE.BACKEND_API_HOST },
    {
      revalidateOnMount: true,
      fallbackData: options.fallbackData,
      onSuccess: (dataFetchedOnClient) => {
        if (dataFetchedOnClient) {
          updateIsSkuFetched(true);
        }
      }
    }
  );

  const {
    data: uamData,
    isValidating: isUamValidating,
    mutate: refetchUam
  } = useFetch(
    isLoggedIn && !isSpecial && (!isGoalSubscribed || isCbt) ? uamKey : null,
    {},
    {
      revalidateOnMount: true,
      onSuccess: (dataFetchedOnClient) => {
        if (dataFetchedOnClient) {
          updateIsUamFetched(true);
        }
      }
    }
  );

  const { actionAllowed } = uamData ?? {};
  const isUamAccessPresent = actionAllowed?.['programme:testSeriesView'];

  const purchasedSKUs =
    data.metaInfo?.latestUserSkusTypeWise?.[TEST_SERIES_CONTENT_TYPE] ?? [];
  const isSkuAvailable = data.skus?.length > 0;
  const isPurchased =
    purchasedSKUs.length > 0 && purchasedSKUs[0]?.objectUid === testSeriesUID;
  const isExpired = isPurchased && purchasedSKUs[0]?.timeRemaining <= 0;
  const expiryTime = purchasedSKUs[0]?.expiredAt;
  const subscription = purchasedSKUs[0]?.subscription;

  const availableSku = data.skus?.[0] ?? {};
  const hasStoreAccess = isUamAccessPresent || (isPurchased && !isExpired);
  const hasSkuAccess = isCbt
    ? hasStoreAccess
    : !isGoalSubscribed && hasStoreAccess;

  return {
    key,
    skuData: data || emptyObject,
    isValidating: isValidating || isUamValidating,
    mutate,
    refetchUam,
    isSkuAvailable,
    isPurchased,
    isExpired,
    expiryTime,
    availableSku,
    hasSkuAccess,
    subscription,
    ...rest
  };
};

export default useSkuDetails;
